import { FilterOutlined } from '@ant-design/icons';
import { CategoryNames, RecommendedProductFamily } from '@omniafishing/core';
import { Col, Modal, Row } from 'antd';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useResponsive } from '../../hooks/use_responsive';
import { getProductFamilies } from '../../redux/landing_page';
import { CollapsiblePanel } from '../collapsible_panel/collapsible_panel';
import { ContentWrapper } from '../content_wrapper/content_wrapper';
import { OmniaButton } from '../omnia_button/omnia_button';
import { SEO } from '../seo/seo';
import { ShopNav } from '../shop_nav/shop_nav';
import headerBg from './ice-header.jpg';
import styles from './ice_landing_page.less';
import { ProductFamilyCarouselContainer } from './product_family_carousel_container';

const getFamiliesBySubcategory = (families: RecommendedProductFamily[], subcategory: string) => {
  return families.filter(
    (recommendedFamily) => recommendedFamily.product_family.subcategory === subcategory
  );
};

const getFamiliesByCategoryAndTechnique = (
  families: RecommendedProductFamily[],
  category: CategoryNames,
  style: string
) => {
  return families.filter((recommendedFamily) => {
    const inCategory = recommendedFamily.product_family.category === category;
    const hasStyle = recommendedFamily.products.some(
      (product) => product.annotations.techniques.indexOf(style) > -1
    );
    return inCategory && hasStyle;
  });
};

export const IceLandingPage = () => {
  const recommendedProductFamilies = useSelector(getProductFamilies);
  const { isMobile } = useResponsive();
  const [filtersOpen, setFiltersOpen] = useState(false);

  const carousels = [
    {
      title: 'Ice Fishing Augers',
      productFamilies: getFamiliesBySubcategory(recommendedProductFamilies, 'accessories-iceauger'),
      carouselType: 'single',
    },
    {
      title: 'Ice Fishing Accessories',
      productFamilies: getFamiliesBySubcategory(
        recommendedProductFamilies,
        'accessories-iceaccessories'
      ),
      carouselType: 'single',
    },
    {
      title: 'Ice Fishing Rods',
      productFamilies: getFamiliesBySubcategory(recommendedProductFamilies, 'rods-icefishing'),
      carouselType: 'single',
    },
    {
      title: 'Ice Fishing Reels',
      productFamilies: getFamiliesBySubcategory(recommendedProductFamilies, 'reels-icefishing'),
      carouselType: 'double',
    },
    {
      title: 'Ice Fishing Lines',
      productFamilies: getFamiliesBySubcategory(recommendedProductFamilies, 'lines-iceline'),
      carouselType: 'double',
    },
    {
      heading: 'Ice Fishing Lures',
      title: 'Ice Fishing Jigs',
      productFamilies: getFamiliesByCategoryAndTechnique(
        recommendedProductFamilies,
        'jigs',
        'ice_jigs'
      ),
      carouselType: 'single',
    },
    {
      title: 'Ice Fishing Spoons',
      productFamilies: getFamiliesByCategoryAndTechnique(
        recommendedProductFamilies,
        'hardbaits',
        'ice_spoons'
      ),
      carouselType: 'single',
    },
    {
      title: 'Ice Fishing Blades',
      productFamilies: getFamiliesBySubcategory(recommendedProductFamilies, 'hardbaits-bladebaits'),
      carouselType: 'single',
    },
    {
      title: 'Ice Fishing Soft Baits',
      productFamilies: getFamiliesBySubcategory(recommendedProductFamilies, 'softbaits-panfish'),
      carouselType: 'single',
    },
    {
      title: 'Ice Fishing Bobbers',
      productFamilies: getFamiliesBySubcategory(recommendedProductFamilies, 'terminal-bobbers'),
      carouselType: 'single',
    },
    {
      heading: 'Ice Fishing Apparel',
      title: 'Ice Fishing Apparel',
      productFamilies: getFamiliesBySubcategory(recommendedProductFamilies, 'apparel-icefishing'),
      carouselType: 'single',
    },
    {
      heading: 'Ice Fishing Shelters',
      title: 'Ice Fishing Shelters',
      productFamilies: getFamiliesBySubcategory(
        recommendedProductFamilies,
        'accessories-iceshelters'
      ),
      carouselType: 'single',
    },
  ];

  const CarouselsSection = (
    <section className={styles.carouselsSection}>
      {carousels.slice(0, 3).map((carousel) => (
        <React.Fragment key={carousel.title}>
          <ProductFamilyCarouselContainer carousel={carousel} />
        </React.Fragment>
      ))}
      <Row gutter={24}>
        <Col xs={24} sm={12}>
          <ProductFamilyCarouselContainer carousel={carousels[3]} />
        </Col>
        <Col xs={24} sm={12}>
          <ProductFamilyCarouselContainer carousel={carousels[4]} />
        </Col>
      </Row>
      {carousels.slice(5, carousels.length).map((carousel) => (
        <React.Fragment key={carousel.title}>
          <ProductFamilyCarouselContainer carousel={carousel} />
        </React.Fragment>
      ))}
    </section>
  );

  const shopPanel = (
    <CollapsiblePanel title="Shop" defaultOpenDesktop={true} defaultOpenMobile={true}>
      <ShopNav />
    </CollapsiblePanel>
  );

  return (
    <>
      <SEO title="Ice Fishing" />
      <ContentWrapper mobileNoPadding>
        <header style={{ backgroundImage: `url(${headerBg})` }} className={styles.header}>
          <h1 className={styles.pageHeading}>ICE FISHING</h1>
        </header>
      </ContentWrapper>
      <ContentWrapper tag="section">
        <Row gutter={0}>
          <Col xs={24} sm={6}>
            {isMobile ? (
              <>
                <section className={styles.topFiltersMobile}>
                  <OmniaButton
                    kind="tertiary"
                    size="md"
                    onClick={() => {
                      setFiltersOpen(true);
                    }}
                  >
                    <FilterOutlined />
                    Filters
                  </OmniaButton>
                </section>
                <Modal
                  open={filtersOpen}
                  onCancel={() => {
                    setFiltersOpen(false);
                  }}
                  footer={
                    <OmniaButton
                      onClick={() => {
                        setFiltersOpen(false);
                      }}
                      kind="secondary"
                      size="md"
                    >
                      Done
                    </OmniaButton>
                  }
                >
                  {shopPanel}
                </Modal>
              </>
            ) : (
              shopPanel
            )}
          </Col>
          <Col xs={24} sm={18}>
            {CarouselsSection}
          </Col>
        </Row>
      </ContentWrapper>
    </>
  );
};
